import styled, { css, useTheme } from "styled-components";
import {
	useState,
	useEffect,
	useRef,
	cloneElement,
	useContext,
	useCallback,
} from "react";
import { CSSTransition } from "react-transition-group";
import { StyledHoverMenu } from "../HoverMenu/HoverMenu";
import Icon from "../Icon/Icon";
import { defaultBoxShadow } from "../../assetsConfig";
import { useSelector, useDispatch } from "react-redux";
import { addAlert, hideAlert } from "./actions";
import { RootState } from "../../store";
import { Dispatch } from "redux";
import { AddAlertAction, Alert, AlertActionTypes } from "./types";
import { v4 as uuidv4 } from "uuid";

export const useAlert = () => {
	const dispatch: Dispatch<AlertActionTypes> = useDispatch();

	const removeAlert = (id: string) => {
		dispatch(hideAlert(id));
	};

	const addNewAlert = (props: {
		type: "success" | "error" | "alert";
		message: string;
		autoHide?: boolean;
	}): Alert => {
		// auto hide alerts by default
		const autoHide = props.autoHide !== undefined ? props.autoHide : true;

		const newAlert: Alert = {
			id: uuidv4(),
			type: props.type,
			message: props.message,
			autoHide: autoHide,
		};

		dispatch(addAlert(newAlert));

		if (autoHide) {
			// hide alert after 2.5s
			setTimeout(() => {
				removeAlert(newAlert.id);
			}, 2500);
		}

		return newAlert;
	};

	return { addNewAlert, removeAlert };
};

export const StyledInfoPopup = styled.div<{
	type: "error" | "success" | "alert";
	index: number;
	autoHide: boolean;
}>`
	position: fixed;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
	padding: 1rem;
	background: ${({ theme }) => theme.colorBackgroundLightLight};
	z-index: 999999999999999999;
	box-shadow: ${defaultBoxShadow};

	min-width: 300px;
	height: 52px;
	right: -2rem;
	${(p) => `bottom: calc(${p.index} * 62px + 2rem)`};
	/* bottom: 2rem; */

	${(p) =>
		p.autoHide
			? css`
					animation: enter-leave 2.6s ease;
			  `
			: css`
					animation: enter 2.6s ease forwards;
			  `};

	transition: all 300ms ease;

	@keyframes enter-leave {
		0% {
			right: -2rem;
			opacity: 0;
		}
		7.5% {
			right: 10px;
			opacity: 1;
		}
		92.5% {
			right: 10px;
			opacity: 1;
		}
		100% {
			right: -2rem;
			opacity: 0;
		}
	}

	@keyframes enter {
		0% {
			right: -2rem;
			opacity: 0;
		}
		7.5% {
			right: 10px;
			opacity: 1;
		}
		100% {
			right: 10px;
			opacity: 1;
		}
	}

	/* caret up  */
	&:before {
		content: "";
		position: absolute;
		left: 0;
		top: 0;
		width: 5px;
		height: 100%;
		background: ${(p) =>
			p.type === "success"
				? p.theme.colorSuccess
				: p.type === "alert"
				? p.theme.colorActivation
				: p.theme.colorDanger};
	}
`;

const Alerts = () => {
	const theme = useTheme();
	const { removeAlert } = useAlert();
	const alerts = useSelector((state: RootState) => state.alerts.alerts);

	return (
		<>
			{alerts && alerts.length > 0
				? alerts.map((alert, index) => {
						return (
							<StyledInfoPopup
								key={alert.id}
								type={alert.type}
								index={index}
								autoHide={alert.autoHide}
							>
								{alert.message}
								<Icon
									icon="close"
									width="20px"
									color={theme.colorCopyLight}
									onClick={() => removeAlert(alert.id)}
								/>
							</StyledInfoPopup>
						);
				  })
				: null}
		</>
	);
};

export default Alerts;
